import { Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  BUTTON,
  CONSUMER_ACCOUNT_CREATION,
  DISABLED,
  EMAIL,
  ENABLE_ACCOUNT,
  FORGOT_PASSWORD,
  INITIAL,
  LOG_IN,
  PROVIDER_ACCOUNT_CREATION,
  REGISTER_ACTION,
  SECURITY_PREFERENCES_ROUTE,
  TEXT_SECONDARY,
} from "../../Constants/CommonConstants";
import {
  ADD_OR_EDIT_VERIFICATION,
  CONSUMER_RESEND_OTP_LINK_HELPER_TEXT,
  LOGIN_RESEND_OTP_LINK_HELPER_TEXT,
  RESEND_OTP_LIMIT_EXHAUSTED,
  USER_ACTION_RESEND,
  VERIFY_ACCOUNT_CODE_NOT_RECEIVED,
  VERIFY_ACCOUNT_RESEND_CODE_HELPER_TEXT,
  VERIFY_ACCOUNT_RESEND_CODE_LINK,
} from "../../Constants/VerifyAccountConstants";
import { PHONE_NUMBER } from "../../Constants/NameAndContactConstants";
import { postAccount } from "../../Features/AccountCreationSlice";
import {
  postRegisterEditVerificationMethod,
  postSendOTP,
} from "../../Features/LoginSlice";
import { postProviderResendOTP } from "../../Features/ProviderAccountCreationSlice";
import { manageVerificationSelector } from "../../Selectors/VerificationSelector";
import {
  StyledResendOTPHelperText,
  StyledResendOTPLink,
  StyledResendOTPLinkBox,
} from "../../Styles/Login/VerifyAccount/VerifyAccountStyles";
import { manageMyProfileSelector } from "../../Selectors/MyProfileSelector";

/**
 * @description Displays the link for resending OTP
 * @param {string} previousScreen specifies the previous screen for Verify Account which can be Login, Review Info, Forgot Password or Enable Account
 * @returns {ReactNode} Renders the Resend OTP link to the user
 */
export const ResendOTP = ({ previousScreen, selectedVerificationMethod }) => {
  const dispatch = useDispatch();

  const intl = useIntl();

  const { sendOTPDescription, authToken, email, phoneNo } = useSelector(
    (state) => manageVerificationSelector(state, previousScreen)
  );

  const { addEditEmailLanguage } = useSelector((state) =>
    manageMyProfileSelector(state)
  );

  const handleAddOrEditResendOTP = () => {
    const verificationMethod =
      selectedVerificationMethod === EMAIL ? EMAIL : PHONE_NUMBER;
    let payload = {
      otpType: selectedVerificationMethod,
      [verificationMethod]:
        selectedVerificationMethod === EMAIL ? email : phoneNo,
    };
    if(selectedVerificationMethod === EMAIL) {
      payload.prefEmailLanguage = addEditEmailLanguage?.description
    }
    dispatch(
      postRegisterEditVerificationMethod({
        data: payload,
        apiAction: REGISTER_ACTION,
      })
    );
  };

  const handleResendLinkClick = () => {
    switch (previousScreen) {
      case CONSUMER_ACCOUNT_CREATION:
        dispatch(
          postAccount({ authToken: authToken, userAction: USER_ACTION_RESEND })
        );
        break;
      case PROVIDER_ACCOUNT_CREATION:
        dispatch(
          postProviderResendOTP({
            authToken: authToken,
            userAction: USER_ACTION_RESEND,
          })
        );
        break;
      case LOG_IN:
      case FORGOT_PASSWORD:
      case ENABLE_ACCOUNT:
        dispatch(postSendOTP({ otpType: selectedVerificationMethod }));
        break;
      case ADD_OR_EDIT_VERIFICATION:
      case SECURITY_PREFERENCES_ROUTE:
        handleAddOrEditResendOTP();
        break;
      default:
        break;
    }
  };

  return (
    <Stack spacing={{ md: 1 }}>
      <Typography id={VERIFY_ACCOUNT_CODE_NOT_RECEIVED}>
        {intl.formatMessage({ id: VERIFY_ACCOUNT_CODE_NOT_RECEIVED })}
      </Typography>
      <StyledResendOTPLinkBox>
        <StyledResendOTPLink
          component={BUTTON}
          type={BUTTON}
          id={VERIFY_ACCOUNT_RESEND_CODE_LINK}
          onClick={handleResendLinkClick}
          linkType={
            sendOTPDescription === RESEND_OTP_LIMIT_EXHAUSTED
              ? DISABLED
              : INITIAL
          }
          disabled={sendOTPDescription === RESEND_OTP_LIMIT_EXHAUSTED}
        >
          {intl.formatMessage({
            id: VERIFY_ACCOUNT_RESEND_CODE_LINK,
          })}
        </StyledResendOTPLink>
        {sendOTPDescription === RESEND_OTP_LIMIT_EXHAUSTED &&
          (previousScreen === CONSUMER_ACCOUNT_CREATION ||
          previousScreen === PROVIDER_ACCOUNT_CREATION ? (
            <StyledResendOTPHelperText
              color={TEXT_SECONDARY}
              id={VERIFY_ACCOUNT_RESEND_CODE_HELPER_TEXT}
            >
              {intl.formatMessage({
                id: CONSUMER_RESEND_OTP_LINK_HELPER_TEXT,
              })}
            </StyledResendOTPHelperText>
          ) : (
            <StyledResendOTPHelperText
              color={TEXT_SECONDARY}
              id={VERIFY_ACCOUNT_RESEND_CODE_HELPER_TEXT}
            >
              {intl.formatMessage({
                id: LOGIN_RESEND_OTP_LINK_HELPER_TEXT,
              })}
            </StyledResendOTPHelperText>
          ))}
      </StyledResendOTPLinkBox>
    </Stack>
  );
};

ResendOTP.propTypes = {
  /**
   * specifies the previous screen
   */
  previousScreen: PropTypes.string.isRequired,
  /**
   * specifies the selected verification method
   */
  selectedVerificationMethod: PropTypes.string.isRequired,
};
